import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandHeader from "../../components/BrandHeader";
import { ordersApi } from "../../lib/api";
import { PRODUCT } from "../../lib/constants";

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersApi.list().then((list) => { setOrders(list); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfWeek = startOfDay - (now.getDay() * 86400 * 1000);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

  const timestamps = orders.map((o) => new Date(o.createdAt).getTime());
  const today = timestamps.filter((t) => t >= startOfDay).length;
  const week = timestamps.filter((t) => t >= startOfWeek).length;
  const month = timestamps.filter((t) => t >= startOfMonth).length;

  const paidOrders = orders.filter((o) => o.paymentStatus === "Payment Confirmed").length;
  const pending = orders.filter((o) => o.status === "Pending Payment").length;
  const revenueMonth = orders
    .filter((o) => new Date(o.createdAt).getTime() >= startOfMonth)
    .reduce((acc) => acc + PRODUCT.price, 0);

  const recent = orders.slice(0, 5);

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <BrandHeader adminMode />
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="ph-overline">Dashboard</div>
            <h1 className="font-serif text-4xl text-[#2C0A5E] mt-2">Your atelier, today.</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admin/new-order" className="ph-btn-primary" data-testid="dashboard-new-order">New Order</Link>
            <Link to="/admin/abandoned" className="ph-btn-ghost" data-testid="dashboard-abandoned">Abandoned</Link>
            <Link to="/admin/orders" className="ph-btn-ghost" data-testid="dashboard-view-orders">View Orders</Link>
            <Link to="/admin/inventory" className="ph-btn-ghost" data-testid="dashboard-view-inventory">Inventory</Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Stat label="Orders Today" value={today} testid="stat-today" />
          <Stat label="This Week" value={week} testid="stat-week" />
          <Stat label="This Month" value={month} testid="stat-month" />
          <Stat label="Revenue (Month)" value={`Rs ${revenueMonth.toLocaleString("en-IN")}`} testid="stat-revenue" highlight />
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-5">
          <Stat label="Total Orders" value={orders.length} testid="stat-total" />
          <Stat label="Payment Confirmed" value={paidOrders} testid="stat-paid" />
          <Stat label="Pending Payment" value={pending} testid="stat-pending" />
        </div>

        <div className="ph-card p-6 md:p-8 mt-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl text-[#2C0A5E]">Recent orders</h2>
            <Link to="/admin/orders" className="text-sm text-[#6A0DAD] hover:underline" data-testid="view-all-orders">View all</Link>
          </div>
          {loading ? (
            <p className="text-[#6B5E7E]">Loading...</p>
          ) : recent.length === 0 ? (
            <p className="text-[#6B5E7E]">No orders yet. Use New Order to enter your first WhatsApp customer.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recent-orders-table">
                <thead>
                  <tr className="text-left text-[#6B5E7E] border-b border-[rgba(106,13,173,0.1)]">
                    <th className="py-3 pr-3">Order</th>
                    <th className="py-3 pr-3">Customer</th>
                    <th className="py-3 pr-3">Email</th>
                    <th className="py-3 pr-3">Element</th>
                    <th className="py-3 pr-3">Status</th>
                    <th className="py-3 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((o) => (
                    <tr key={o.id} className="border-b border-[rgba(106,13,173,0.05)]">
                      <td className="py-3 pr-3 font-mono text-[#2C0A5E]">{o.orderNumber}</td>
                      <td className="py-3 pr-3 text-[#2C0A5E]">{o.fullName}</td>
                      <td className="py-3 pr-3 text-[#6B5E7E] truncate max-w-[160px]">{o.email || "-"}</td>
                      <td className="py-3 pr-3 capitalize text-[#6A0DAD]">{o.dominantElement}</td>
                      <td className="py-3 pr-3 text-[#6B5E7E]">{o.status}</td>
                      <td className="py-3 pr-3">
                        <Link to={`/admin/orders/${o.id}`} className="text-[#6A0DAD] hover:underline" data-testid={`dashboard-order-${o.id}`}>Open</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div className="ph-card p-6" data-testid={testid}>
      <div className="ph-overline">{label}</div>
      <div className={`font-serif mt-2 ${highlight ? "text-3xl text-[#6A0DAD]" : "text-3xl text-[#2C0A5E]"}`}>{value}</div>
    </div>
  );
}
